import { IAddRepresentativeFormFields } from 'shared/interfaces/IRepresentatives';
import { RepresentationType } from 'shared/enums/RepresentationType';
import { FormikProps, useFormik } from 'formik';
import { useCallback } from 'react';
import { useAddRepresentativesAgencyRequiredField } from './useAddRepresentativesAgencyRequiredField';
import { representativeService } from 'shared/services/representativesService';
import { useQueryClient } from 'react-query';
import { REPRESENTATIVES_QUERY_STRING } from 'shared/constants/REPRESENTATIVES_QUERY_STRING';
import { ERROR_MESSAGE } from '../const/ERROR_MESSAGE';
import {
  createProfileValidationSchema,
  KeyOfAddRepresentativesForm,
  OptionalValuesProps,
  SetFieldValueValueProps,
} from '../model/AddRepresentativeSchema';
import { useRepresentativesModal } from 'pages/Representatives/context/RepresentativesModalProvider';

const { createRepresentatives } = representativeService();

/* 
  Custom hook for managing the form state of adding representatives
  It handles form validation, setting field values, 
  and determining if the form can be saved based on certain conditions (e.g., agency requirements, country, profile type)
*/
export const useAddRepresentativesFormik = (
  userCountry: string,
  hasFreelance: boolean,
  medium_id: string,
  handleOnClose: VoidFunction,
) => {
  // Custom hook to manage whether the agency requires division and agency code
  const { agencyRequiredField, handleAgencyRequiredField } = useAddRepresentativesAgencyRequiredField();
  const { handleOpen, handleSetMessage } = useRepresentativesModal();
  const { mutateAsync, isLoading: creationLoading } = createRepresentatives();
  const queryClient = useQueryClient();

  // Initial Payload of the form
  const initialValues: IAddRepresentativeFormFields = {
    representation_type: RepresentationType.AGENCY_REPRESENTATION,
    country: userCountry.toLowerCase() === 'au' ? 'Australia' : 'United States',
    note: '',
    agency_id: '',
    medium_id,
    confirmed_agreement: false,
    profile_type: '',
    access_code: '',
    division_ids: [],
  };

  // Submit Creation Action
  const handleSubmit = async (values: IAddRepresentativeFormFields) => {
    const {
      representation_type,
      profile_type,
      country,
      agency_id,
      access_code,
      division_ids = [],
      confirmed_agreement,
    } = values;
    // Common Payload, this can be used when the representation type is non_represented
    const common_payload = {
      representation_type,
      confirmed_agreement,
      profile_type,
      country,
    };

    // Payload for agency_represented
    const divisions =
      division_ids[0]?.id === 'Any' || division_ids?.length === 0
        ? {}
        : { division_ids: division_ids.map((division) => division.id) };

    // Payload to be sent,
    const payload = {
      ...common_payload,
      /*  If Agency Representation add this payload */
      ...(representation_type !== 'Non-Represented' && {
        agency_id,
        access_code,
        ...divisions,
      }),
    };

    await mutateAsync(payload, {
      onSuccess: async () => {
        await queryClient.invalidateQueries([REPRESENTATIVES_QUERY_STRING.representatives_multiple_profile]);
        handleOnClose();
      },
      // Erro Handling
      onError: async (error) => {
        const { data } = error?.response || {};
        const errors = data?.errors || {};
        const display_error = errors.agency_id?.[0]
          ? ERROR_MESSAGE.agency_exist
          : errors.access_code?.[0]
            ? ERROR_MESSAGE.agency_code
            : errors.division_ids?.[0]
              ? ERROR_MESSAGE.division_exist
              : data?.error
                ? ERROR_MESSAGE.unprocessable_entity
                : ERROR_MESSAGE.general;
        // Open the error modal
        handleSetMessage(display_error.header, display_error.message, 'Close');
        handleOpen();
      },
    });
  };

  // Formik initialization
  const formikForm: FormikProps<IAddRepresentativeFormFields> = useFormik({
    initialValues,
    validationSchema: createProfileValidationSchema,
    onSubmit: handleSubmit,
  });

  // Callback to set a field value dynamically in Formik
  const handleSetFieldValue = useCallback(
    (key: KeyOfAddRepresentativesForm, value: SetFieldValueValueProps, optionalValues?: OptionalValuesProps) => {
      /* If any of this condition is true reset the division_id, agency id and access code
        because every country has different agency, and if the current representation type is agency
        division_id, agency_id and access code is not needed, and if the client clears the agency field,
        clear all ofthem
      */
      if (
        key === 'representation_type' ||
        key === 'country' ||
        (key === 'agency_id' && value !== formikForm.values.agency_id) ||
        (key === 'agency_id' && value === '')
      ) {
        formikForm.setFieldValue('division_ids', initialValues.division_ids);
        formikForm.setFieldValue('agency_id', initialValues.agency_id);
        formikForm.setFieldValue('access_code', initialValues.access_code);
        handleAgencyRequiredField({
          code_required: false,
          division_required: false,
        });
      }

      // If key is agency_id and has optional values, it means this is valid, this is connected to useRepresentativesAgencyInput.ts
      if (key === 'agency_id' && optionalValues) {
        const { required_access_code = false, division_required = false } = optionalValues;
        handleAgencyRequiredField({
          code_required: required_access_code,
          division_required: division_required,
        });
      }

      formikForm.setFieldValue(key, value);
    },
    [],
  );

  const resetAddFormValues = useCallback(() => {
    formikForm.resetForm();
    handleAgencyRequiredField({
      code_required: false,
      division_required: false,
    });
  }, []);

  // Extract values from Formik state for easier reference
  const {
    representation_type,
    country,
    profile_type,
    agency_id,
    division_ids = [],
    access_code,
  } = formikForm?.values || {};

  // Determine whether the form can be saved based on agency requirements and other field values
  const allowedToSave =
    representation_type === 'Agency Representation'
      ? representation_type &&
        country &&
        profile_type &&
        agency_id &&
        (agencyRequiredField?.code_required ? Boolean(access_code) : true) &&
        (agencyRequiredField?.division_required ? division_ids?.length > 0 : true)
      : !hasFreelance && representation_type && country && profile_type;

  return {
    formikForm,
    agencyRequiredField,
    allowedToSave,
    creationLoading,
    handleSubmit: formikForm.handleSubmit,
    handleAgencyRequiredField,
    handleSetFieldValue,
    resetAddFormValues,
  };
};
